import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18next from 'i18next'
import { documentService } from '@/services/api'
import { queryKeys } from '@/config/queryKeys'
import type { GetDocumentsParams } from '@/services/api/document/document.static'

export const useInfiniteDocuments = (params?: Omit<GetDocumentsParams, 'page'>) => {
  return useInfiniteQuery({
    queryKey: queryKeys.documents.list({ ...params }),
    queryFn: ({ pageParam }) =>
      documentService.getDocuments({ ...params, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.total / lastPage.limit)
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined
    },
  })
}

export const useDocuments = (params?: GetDocumentsParams) => {
  return useQuery({
    queryKey: queryKeys.documents.list({ ...params }),
    queryFn: () => documentService.getDocuments(params),
  })
}

export const useDocument = (id: string) => {
  return useQuery({
    queryKey: queryKeys.documents.detail(id),
    queryFn: () => documentService.getDocument(id),
    enabled: !!id,
  })
}

export const useUploadDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: documentService.uploadDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.lists() })
      toast.success(i18next.t('documents:documentCreated'))
    },
    onError: () => {
      toast.error(i18next.t('documents:uploadFailed'))
    },
  })
}

export const useBulkUploadDocuments = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: documentService.bulkUploadDocuments,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.lists() })
      toast.success(i18next.t('documents:documentsUploaded'))
    },
    onError: () => {
      toast.error(i18next.t('documents:uploadFailed'))
    },
  })
}

export const useUpdateDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof documentService.updateDocument>[1]
    }) => documentService.updateDocument(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) })
      toast.success(i18next.t('documents:documentUpdated'))
    },
    onError: () => {
      toast.error(i18next.t('common:errors.saveFailed'))
    },
  })
}

export const useArchiveDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => documentService.archiveDocument(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) })
      toast.success(i18next.t('documents:documentArchived'))
    },
    onError: () => {
      toast.error(i18next.t('common:errors.saveFailed'))
    },
  })
}

export const useUnarchiveDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => documentService.unarchiveDocument(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(id) })
      toast.success(i18next.t('documents:documentUnarchived'))
    },
    onError: () => {
      toast.error(i18next.t('common:errors.saveFailed'))
    },
  })
}

export const useDeleteDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => documentService.deleteDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.lists() })
      toast.success(i18next.t('documents:documentDeleted'))
    },
    onError: () => {
      toast.error(i18next.t('common:errors.saveFailed'))
    },
  })
}

export const useDownloadUploadedDocument = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const { signedUrl, filename } = await documentService.getDownloadUrl(id)
      const response = await fetch(signedUrl)
      const blob = await response.blob()
      return { blob, filename }
    },
    onSuccess: ({ blob, filename }) => {
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
    },
    onError: () => {
      toast.error(i18next.t('documents:uploadFailed'))
    },
  })
}
